import { useEffect, useState } from "react";
import api, { errMsg, formatDate, formatTL, formatNumber } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Plus, Trash2, X, Gauge, Fuel, TrendingDown, Route, ChevronDown, ChevronUp, Upload } from "lucide-react";
import { toast } from "sonner";

const monthLabel = (m) => {
  if (!m || m === "unknown") return "Belirsiz";
  const [y, mo] = m.split("-");
  const names = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
  return `${names[parseInt(mo, 10) - 1] || mo} ${y}`;
};

const emptyRow = () => ({
  vehicle_id: "", driver_id: "",
  date: new Date().toISOString().slice(0, 10),
  km_start: "", km_end: "", fuel_liters: "", fuel_cost: "", notes: ""
});

export default function KmLogsPage() {
  const [months, setMonths] = useState([]);
  const [grand, setGrand] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyRow());
  const [showBulk, setShowBulk] = useState(false);
  const [bulkRows, setBulkRows] = useState([emptyRow()]);

  const load = async () => {
    try {
      const [m, v, d] = await Promise.all([
        api.get("/km-logs/by-month"),
        api.get("/vehicles"),
        api.get("/drivers"),
      ]);
      setMonths(m.data.months || []);
      setGrand(m.data.grand || {});
      setVehicles(v.data); setDrivers(d.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (m) => {
    const s = new Set(expanded); if (s.has(m)) s.delete(m); else s.add(m); setExpanded(s);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/km-logs", {
        ...form,
        km_start: Number(form.km_start) || 0,
        km_end: Number(form.km_end) || 0,
        fuel_liters: Number(form.fuel_liters) || 0,
        fuel_cost: Number(form.fuel_cost) || 0,
      });
      toast.success("Km kaydı eklendi");
      setShowForm(false); setForm(emptyRow()); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    try { await api.delete(`/km-logs/${id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const updateBulk = (i, key, val) => {
    const arr = [...bulkRows];
    arr[i] = { ...arr[i], [key]: val };
    setBulkRows(arr);
  };

  const submitBulk = async () => {
    const valid = bulkRows.filter(r => r.vehicle_id && r.date && Number(r.km_end) > Number(r.km_start) && Number(r.fuel_liters) > 0);
    if (valid.length === 0) { toast.error("En az bir geçerli satır girin (araç, tarih, km bitiş > km başlangıç, litre > 0)"); return; }
    try {
      const { data } = await api.post("/km-logs/bulk", { rows: valid });
      toast.success(`${data.inserted} km kaydı eklendi${data.skipped ? ` (${data.skipped} atlandı)` : ""}`);
      setShowBulk(false); setBulkRows([emptyRow()]); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // Live compute for form
  const distance = Math.max(0, (Number(form.km_end) || 0) - (Number(form.km_start) || 0));
  const avg = distance > 0 && Number(form.fuel_liters) > 0 ? ((Number(form.fuel_liters) / distance) * 100).toFixed(2) : "0.00";
  const kmPerLit = Number(form.fuel_liters) > 0 ? (distance / Number(form.fuel_liters)).toFixed(2) : "0.00";
  const tlPerKmF = distance > 0 ? ((Number(form.fuel_cost) || 0) / distance).toFixed(2) : "0.00";

  return (
    <>
      <PageHeader
        title="Km & Yakıt Kayıtları"
        subtitle="Araç bazlı km girişleri; aylık gruplu görünüm ile ortalama yakıt tüketimi ve ₺/km analizi."
        testid="km-logs-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="km-bulk-btn" onClick={() => { setBulkRows([emptyRow()]); setShowBulk(true); }} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5]">
              <Upload className="w-3.5 h-3.5" /> Toplu Ekle
            </button>
            <button data-testid="km-logs-add-btn" onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2] transition-colors">
              <Plus className="w-4 h-4" strokeWidth={2.2} /> Yeni Kayıt
            </button>
          </div>
        }
      />

      {/* Grand KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Km</div>
          <div className="num font-bold text-xl mt-1">{formatNumber(grand.total_km || 0)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Litre</div>
          <div className="num font-bold text-xl mt-1">{formatNumber(grand.total_liters || 0, 2)} L</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Yakıt Gideri</div>
          <div className="num font-bold text-xl mt-1">{formatTL(grand.total_cost || 0)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ort. L/100km</div>
          <div className="num font-bold text-xl mt-1 text-[#F59E0B]">{formatNumber(grand.avg_l_100km || 0, 2)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ort. ₺/Km</div>
          <div className="num font-bold text-xl mt-1 text-[#DC2626]">{formatTL(grand.tl_per_km || 0)}</div>
        </div>
      </div>

      {/* Monthly groups */}
      <div className="space-y-3">
        {months.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] p-16 text-center text-sm text-neutral-500">
            <Route className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
            Henüz km kaydı yok. Yeni kayıt veya toplu ekleme yapın.
          </div>
        ) : months.map(g => {
          const isOpen = expanded.has(g.month);
          return (
            <div key={g.month} className="bg-white border border-[#E5E5E5] rounded-md" data-testid={`km-month-${g.month}`}>
              <button onClick={() => toggle(g.month)} className="w-full px-5 py-4 flex items-center justify-between text-left">
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
                  <div>
                    <div className="font-heading font-bold text-lg">{monthLabel(g.month)}</div>
                    <div className="text-xs text-neutral-500 mt-0.5"><span className="num font-semibold">{g.count}</span> kayıt · <span className="num">{formatNumber(g.total_km)}</span> km · <span className="num">{formatNumber(g.total_liters, 1)}</span> L</div>
                  </div>
                </div>
                <div className="flex items-center gap-5 flex-wrap">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">L/100km</div>
                    <div className="num font-bold text-sm text-[#F59E0B]">{formatNumber(g.avg_l_100km, 2)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">₺/Km</div>
                    <div className="num font-bold text-sm text-[#DC2626]">{formatTL(g.tl_per_km)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Yakıt Gid.</div>
                    <div className="num font-bold text-sm">{formatTL(g.total_cost)}</div>
                  </div>
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-[#F5F5F5] p-4 space-y-4">
                  {/* Vehicle breakdown */}
                  {g.by_vehicle.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-semibold">Araç Bazlı Performans</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {g.by_vehicle.map(v => (
                          <div key={v.vehicle_id || "novid"} className="border border-[#F5F5F5] p-3 bg-[#FAFAFA]">
                            <div className="font-semibold text-sm truncate">{v.vehicle_name}</div>
                            <div className="grid grid-cols-3 gap-2 mt-2 text-[10px]">
                              <div><span className="text-neutral-500">Km</span><div className="num font-semibold">{formatNumber(v.total_km)}</div></div>
                              <div><span className="text-neutral-500">L/100km</span><div className="num font-semibold text-[#F59E0B]">{formatNumber(v.avg_l_100km, 2)}</div></div>
                              <div><span className="text-neutral-500">₺/Km</span><div className="num font-semibold text-[#DC2626]">{formatTL(v.tl_per_km)}</div></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Driver breakdown */}
                  {g.by_driver.length > 0 && g.by_driver.some(d => d.driver_id) && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-semibold">Şoför Bazlı</div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {g.by_driver.filter(d => d.driver_id).map(d => (
                          <div key={d.driver_id} className="border border-[#F5F5F5] p-2 bg-[#FAFAFA] text-xs">
                            <div className="font-semibold truncate">{d.driver_name}</div>
                            <div className="flex justify-between mt-1 text-neutral-600">
                              <span className="num">{formatNumber(d.total_km)} km</span>
                              <span className="num text-[#F59E0B]">{formatNumber(d.avg_l_100km, 1)}L/100</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Entries */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#FAFAFA]">
                        <tr>
                          {["Tarih","Plaka","Şoför","Km Bşl.","Km Bit.","Mesafe","Litre","₺","L/100km","₺/Km",""].map((h, i) => (
                            <th key={h} className={`px-2 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 3 && i <= 9 ? "text-right" : "text-left"}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {g.entries.map(l => (
                          <tr key={l.id} className="border-t border-[#F5F5F5]" data-testid={`km-log-row-${l.id}`}>
                            <td className="px-2 py-2 num whitespace-nowrap">{formatDate(l.date)}</td>
                            <td className="px-2 py-2 font-semibold">{l.vehicle_name}</td>
                            <td className="px-2 py-2 text-neutral-600">{l.driver_name}</td>
                            <td className="px-2 py-2 text-right num">{formatNumber(l.km_start)}</td>
                            <td className="px-2 py-2 text-right num">{formatNumber(l.km_end)}</td>
                            <td className="px-2 py-2 text-right num font-semibold">{formatNumber(l.distance)}</td>
                            <td className="px-2 py-2 text-right num">{formatNumber(l.fuel_liters, 2)}</td>
                            <td className="px-2 py-2 text-right num">{formatTL(l.fuel_cost)}</td>
                            <td className="px-2 py-2 text-right num text-[#F59E0B] font-semibold">{formatNumber(l.avg_consumption, 2)}</td>
                            <td className="px-2 py-2 text-right num text-[#DC2626] font-semibold">{formatTL(l.tl_per_km || (l.distance > 0 ? l.fuel_cost / l.distance : 0))}</td>
                            <td className="px-2 py-2 text-right"><button onClick={() => remove(l.id)} className="p-1 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3 h-3" /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Single form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-2xl rounded-md shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Yeni Kayıt</div>
                <div className="font-heading font-bold text-lg">Km & Yakıt</div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Araç *</label>
                <select data-testid="km-field-vehicle" required value={form.vehicle_id} onChange={(e) => setForm({...form, vehicle_id: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                  <option value="">Seçiniz</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.plate} — {v.model}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Şoför</label>
                <select data-testid="km-field-driver" value={form.driver_id} onChange={(e) => setForm({...form, driver_id: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                  <option value="">Seçiniz</option>
                  {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tarih *</label>
                <input data-testid="km-field-date" type="date" required value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Km Başlangıç *</label>
                <input data-testid="km-field-start" type="number" step="any" required value={form.km_start} onChange={(e) => setForm({...form, km_start: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Km Bitiş *</label>
                <input data-testid="km-field-end" type="number" step="any" required value={form.km_end} onChange={(e) => setForm({...form, km_end: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Yakıt (L) *</label>
                <input data-testid="km-field-liters" type="number" step="any" required value={form.fuel_liters} onChange={(e) => setForm({...form, fuel_liters: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Yakıt Bedeli (₺)</label>
                <input data-testid="km-field-cost" type="number" step="any" value={form.fuel_cost} onChange={(e) => setForm({...form, fuel_cost: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
              </div>
              <div className="md:col-span-2 bg-[#FAFAFA] border border-[#E5E5E5] p-3 space-y-1.5">
                <div className="flex items-center gap-3">
                  <Gauge className="w-4 h-4 text-[#F59E0B]" />
                  <div className="text-xs text-neutral-600 flex-1">Mesafe: <span className="num font-semibold text-[#0A0A0A]">{formatNumber(distance)} km</span> · Ort. tüketim: <span className="num font-semibold text-[#F59E0B]">{avg} L/100km</span></div>
                </div>
                <div className="flex items-center gap-3">
                  <Fuel className="w-4 h-4 text-[#0020C2]" />
                  <div className="text-xs text-neutral-600 flex-1">Km/Litre: <span className="num font-semibold text-[#0020C2]">{kmPerLit}</span></div>
                </div>
                <div className="flex items-center gap-3">
                  <TrendingDown className="w-4 h-4 text-[#DC2626]" />
                  <div className="text-xs text-neutral-600 flex-1">Km başına yakıt maliyeti: <span className="num font-semibold text-[#DC2626]">₺ {tlPerKmF}</span></div>
                </div>
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="km-submit-btn" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk modal */}
      {showBulk && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowBulk(false)}>
          <div className="bg-white w-full max-w-6xl rounded-md shadow-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu Km & Yakıt Ekleme</div>
                <div className="font-heading font-bold text-lg">Çoklu Satır Girişi</div>
              </div>
              <button onClick={() => setShowBulk(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-neutral-500">Km bitiş &gt; Km başlangıç olmalı. Litre zorunlu.</div>
                <button onClick={() => setBulkRows([...bulkRows, emptyRow()])} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">
                  <Plus className="w-3 h-3" /> Satır Ekle
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-[#E5E5E5]">
                  <thead className="bg-[#FAFAFA]">
                    <tr>
                      {["Tarih *","Araç *","Şoför","Km Başlangıç *","Km Bitiş *","Litre *","Bedel (₺)",""].map(h => (
                        <th key={h} className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.map((r, i) => {
                      const dist = Math.max(0, (Number(r.km_end) || 0) - (Number(r.km_start) || 0));
                      const l100 = dist > 0 && Number(r.fuel_liters) > 0 ? ((Number(r.fuel_liters) / dist) * 100).toFixed(1) : "0";
                      return (
                        <tr key={i} className="border-t border-[#F5F5F5]">
                          <td className="px-2 py-2">
                            <input data-testid={`bulk-km-date-${i}`} type="date" value={r.date} onChange={e => updateBulk(i, "date", e.target.value)} className="w-full px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs num" />
                          </td>
                          <td className="px-2 py-2">
                            <select value={r.vehicle_id} onChange={e => updateBulk(i, "vehicle_id", e.target.value)} className="w-32 px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs">
                              <option value="">Seç</option>
                              {vehicles.map(v => <option key={v.id} value={v.id}>{v.plate}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-2">
                            <select value={r.driver_id} onChange={e => updateBulk(i, "driver_id", e.target.value)} className="w-32 px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs">
                              <option value="">-</option>
                              {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-2">
                            <input data-testid={`bulk-km-start-${i}`} type="number" step="any" value={r.km_start} onChange={e => updateBulk(i, "km_start", e.target.value)} className="w-28 px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs num text-right" />
                          </td>
                          <td className="px-2 py-2">
                            <input data-testid={`bulk-km-end-${i}`} type="number" step="any" value={r.km_end} onChange={e => updateBulk(i, "km_end", e.target.value)} className="w-28 px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs num text-right" />
                            {dist > 0 && <div className="text-[9px] text-neutral-400 text-right mt-0.5"><span className="num">{formatNumber(dist)}</span> km · <span className="num text-[#F59E0B]">{l100}</span> L/100</div>}
                          </td>
                          <td className="px-2 py-2">
                            <input data-testid={`bulk-km-liters-${i}`} type="number" step="any" value={r.fuel_liters} onChange={e => updateBulk(i, "fuel_liters", e.target.value)} className="w-24 px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs num text-right" />
                          </td>
                          <td className="px-2 py-2">
                            <input type="number" step="any" value={r.fuel_cost} onChange={e => updateBulk(i, "fuel_cost", e.target.value)} className="w-28 px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs num text-right" />
                          </td>
                          <td className="px-2 py-2">
                            {bulkRows.length > 1 && <button onClick={() => setBulkRows(bulkRows.filter((_, idx) => idx !== i))} className="p-1 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3 h-3" /></button>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button onClick={() => setShowBulk(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="bulk-km-submit" onClick={submitBulk} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">Toplu Kaydet</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
